/**
 * Groovi password form bridge
 * ---------------------------------------------------------------------------
 * The password gate's signup form is a Shopify Forms app embed: it loads a
 * remote bundle and renders inside a <shopify-forms-embed> shadow root, which
 * is slow to appear. This bridge replaces that slow first-paint with an
 * instant, brand-styled HTML form the visitor can fill immediately, then drives
 * the hidden Shopify form in the background so the backend still records the
 * lead (list wiring, hCaptcha token, anti-spam — all untouched).
 *
 * Behaviour:
 *   1. The Shopify form is moved off-screen (visually hidden, NOT display:none —
 *      hCaptcha's iframe must stay rendered to execute).
 *   2. Our form is injected in its place and is usable on first paint.
 *   3. Every keystroke mirrors into the real fields (#first_name / #email /
 *      #phone_number) via the native value setter + input event.
 *   4. On submit: e-mail typo check ("Você quis dizer…?") → mirror final values
 *      → click the real submit (invisible hCaptcha runs) → observe the outcome →
 *      show our own success state.
 *
 * Fallback (never lose data) — three layers:
 *   a. If this script never runs, the native Shopify form shows as-is.
 *   b. If the submit chain breaks (field renamed, no mount, no outcome), we
 *      reveal the real Shopify form PRE-FILLED and hide ours.
 *   c. E-mail typos are a soft nudge, never a data-losing block.
 *
 * pt-BR is hardcoded: this gate is primary-locale only.
 */
(function () {
  'use strict';

  var WRAPPER_SEL = '.password-main-content .shopify-app-block';
  var MOUNT_TIMEOUT = 10000; // ms to wait for the Shopify form to mount
  // Generous: the real submit (hCaptcha + network) can take well over 9s. If we
  // bail too early we'd reveal the form just as it succeeds → two messages.
  var OUTCOME_TIMEOUT = 20000;

  // Popular BR + global mail providers, for the "did you mean?" suggestion.
  var DOMAINS = [
    'gmail.com', 'hotmail.com', 'hotmail.com.br', 'outlook.com', 'outlook.com.br',
    'yahoo.com', 'yahoo.com.br', 'icloud.com', 'live.com', 'msn.com', 'me.com',
    'bol.com.br', 'uol.com.br', 'terra.com.br', 'globo.com', 'globomail.com',
    'aol.com', 'proton.me', 'protonmail.com'
  ];

  var COPY = {
    name: 'Nome',
    email: 'E-mail',
    // +55 shown only as a draft/example in the placeholder — not forced, so
    // visitors with another country code can type their own.
    phonePlaceholder: '+55 11 98765-4321',
    phoneAria: 'Telefone com DDI e DDD',
    // Faded ghost shown inside the field after the real "+", as a draft example.
    phoneGhost: '55 11 98765-4321',
    submit: 'FICAR POR DENTRO',
    // LGPD consent — must mirror the Shopify form's wording verbatim, since the
    // bridge replaces that form's visible UI and consent has to stay informed.
    consent: 'Ao enviar, você concorda em receber e-mails marketing e mensagens de texto. Veja nossa Política de privacidade e os Termos de serviço para mais informações.',
    sending: 'Enviando…',
    required: 'Preencha todos os campos para continuar.',
    emailInvalid: 'Hmm, esse e-mail parece incompleto.',
    phoneInvalid: 'Confira seu telefone — DDD e número.',
    didYouMean: 'Você quis dizer',
    success: 'Pronto! Você está na lista. A gente te avisa. '
  };

  // Native value setter so framework-controlled inputs register the change.
  var nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype, 'value'
  ).set;

  function setReal(input, value) {
    if (!input) return false;
    try {
      nativeSetter.call(input, value);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    } catch (e) {
      return false;
    }
  }

  // --- e-mail typo detection -------------------------------------------------

  function levenshtein(a, b) {
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (!al) return bl;
    if (!bl) return al;
    var row = [];
    for (var i = 0; i <= bl; i++) row[i] = i;
    for (var x = 1; x <= al; x++) {
      var prev = x;
      for (var y = 1; y <= bl; y++) {
        var val = a[x - 1] === b[y - 1] ? row[y - 1] : Math.min(row[y - 1] + 1, prev + 1, row[y] + 1);
        row[y - 1] = prev;
        prev = val;
      }
      row[bl] = prev;
    }
    return row[bl];
  }

  // Returns a suggested domain when the typed one is 1–2 edits from a known
  // provider (and not an exact match); otherwise null. Conservative on purpose.
  function closestDomain(domain) {
    var best = null, min = Infinity;
    for (var i = 0; i < DOMAINS.length; i++) {
      var d = levenshtein(domain, DOMAINS[i]);
      if (d < min) { min = d; best = DOMAINS[i]; }
    }
    return (min > 0 && min <= 2) ? best : null;
  }

  function syntaxOk(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Returns the corrected full e-mail if a likely typo is found, else null.
  function emailSuggestion(value) {
    var v = (value || '').trim();
    var at = v.lastIndexOf('@');
    if (at < 1) return null;
    var local = v.slice(0, at);
    var domain = v.slice(at + 1).toLowerCase();
    if (!domain) return null;
    var fix = closestDomain(domain);
    return fix ? (local + '@' + fix) : null;
  }

  // --- build the form --------------------------------------------------------

  function buildForm() {
    var form = document.createElement('form');
    form.className = 'groovi-bridge';
    form.noValidate = true;
    form.setAttribute('aria-label', 'Cadastro Groovi');
    form.innerHTML =
      '<div class="groovi-bridge__field">' +
        '<input class="groovi-bridge__input" id="groovi-bridge-name" type="text" ' +
          'name="name" autocomplete="given-name" placeholder="' + COPY.name + '" aria-label="' + COPY.name + '">' +
      '</div>' +
      '<div class="groovi-bridge__field">' +
        '<input class="groovi-bridge__input" id="groovi-bridge-email" type="email" ' +
          'name="email" autocomplete="email" inputmode="email" placeholder="' + COPY.email + '" ' +
          'aria-label="' + COPY.email + '" aria-describedby="groovi-bridge-email-hint">' +
        '<p class="groovi-bridge__hint" id="groovi-bridge-email-hint" hidden></p>' +
      '</div>' +
      '<div class="groovi-bridge__field">' +
        '<div class="groovi-bridge__phone">' +
          '<input class="groovi-bridge__input groovi-bridge__input--phone" id="groovi-bridge-phone" type="tel" value="+" ' +
            'name="phone" autocomplete="tel" inputmode="tel" placeholder="' + COPY.phonePlaceholder + '" ' +
            'aria-label="' + COPY.phoneAria + '">' +
          '<span class="groovi-bridge__phone-ghost" aria-hidden="true">' +
            '<span class="groovi-bridge__phone-ghost-pad">+</span>' + COPY.phoneGhost +
          '</span>' +
        '</div>' +
      '</div>' +
      '<button class="groovi-bridge__submit" type="submit">' + COPY.submit + '</button>' +
      '<p class="groovi-bridge__consent">' + COPY.consent + '</p>' +
      '<p class="groovi-bridge__status" role="status" aria-live="polite" hidden></p>';

    return {
      root: form,
      name: form.querySelector('#groovi-bridge-name'),
      email: form.querySelector('#groovi-bridge-email'),
      phone: form.querySelector('#groovi-bridge-phone'),
      hint: form.querySelector('#groovi-bridge-email-hint'),
      submit: form.querySelector('.groovi-bridge__submit'),
      status: form.querySelector('.groovi-bridge__status')
    };
  }

  // --- shadow-form access ----------------------------------------------------

  function shadowFields(formRoot) {
    var embed = formRoot.querySelector('shopify-forms-embed');
    var sr = embed && embed.shadowRoot;
    if (!sr) return null;
    return {
      embed: embed,
      shadow: sr,
      form: sr.querySelector('form'),
      name: sr.querySelector('#first_name'),
      email: sr.querySelector('#email'),
      phone: sr.querySelector('#phone_number'),
      submit: sr.querySelector('button[type="submit"]')
    };
  }

  function isMounted(f) {
    return !!(f && f.form && f.email);
  }

  function waitForMount(formRoot, onReady, onTimeout) {
    var existing = shadowFields(formRoot);
    if (isMounted(existing)) { onReady(existing); return; }

    var done = false;
    var embed = formRoot.querySelector('shopify-forms-embed');
    var target = (embed && embed.shadowRoot) || embed || formRoot;
    var io = new MutationObserver(function () {
      var f = shadowFields(formRoot);
      if (isMounted(f) && !done) { done = true; io.disconnect(); onReady(f); }
    });
    io.observe(target, { childList: true, subtree: true });
    setTimeout(function () {
      if (!done) { done = true; io.disconnect(); if (onTimeout) onTimeout(); }
    }, MOUNT_TIMEOUT);
  }

  // Best-effort: warm the Shopify Forms bundle so it mounts sooner. The URL is
  // derived from the live loader tag (no hardcoding → no version drift).
  function warmBundle() {
    try {
      var loader = document.querySelector('script[src*="shopify-forms-loader"], script[src*="forms-"][src*="loader.js"]');
      if (!loader || !loader.src) return;
      var url = loader.src.replace(/(shopify-forms-loader(-\w+)*\.js|loader\.js)(\?.*)?$/, 'index.js');
      if (url === loader.src) return;
      if (document.querySelector('link[rel="modulepreload"][href="' + url + '"]')) return;
      var link = document.createElement('link');
      link.rel = 'modulepreload';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    } catch (e) { /* warming is optional */ }
  }

  // --- outcome observation ---------------------------------------------------

  // Resolves to exactly one path: onSuccess (form replaced by a thank-you) or
  // onReveal (a validation error appeared, OR we timed out — in both cases we
  // hand to the real Shopify form, which then shows its OWN message). We never
  // show a note of our own here, so our UI can't contradict Shopify's result.
  function observeOutcome(f, onSuccess, onReveal) {
    var done = false;
    function finish(fn) {
      if (done) return;
      done = true;
      io.disconnect();
      clearTimeout(timer);
      fn();
    }
    function scan() {
      // Error: Shopify marks the invalid FIELDS with aria-invalid. Do NOT key
      // off role="alert" — Shopify's SUCCESS message also uses it, so that would
      // misroute a successful submit to the fallback and skip showSuccess (the
      // heading/subheading hide). Only invalid fields carry aria-invalid.
      if (f.shadow.querySelector('[aria-invalid="true"]')) {
        return finish(onReveal);
      }
      // Success: the form is replaced by a thank-you, so the e-mail input is
      // gone while the shadow root still holds content.
      var formGone = !f.shadow.querySelector('#email') && !f.shadow.querySelector('input[type="email"]');
      if (formGone && (f.shadow.textContent || '').trim().length > 0) {
        return finish(onSuccess);
      }
    }
    var io = new MutationObserver(scan);
    io.observe(f.shadow, { childList: true, subtree: true, attributes: true, characterData: true });
    // Timeout → reveal the real form silently; whatever state it's in (often a
    // slow success) becomes the single source of truth.
    var timer = setTimeout(function () { finish(onReveal); }, OUTCOME_TIMEOUT);
  }

  // --- UI helpers ------------------------------------------------------------

  function showHint(b, suggestion) {
    b.hint.innerHTML = COPY.didYouMean +
      ' <button type="button" class="groovi-bridge__suggest"></button>?';
    var btn = b.hint.querySelector('.groovi-bridge__suggest');
    btn.textContent = suggestion;
    btn.addEventListener('click', function () {
      b.email.value = suggestion;
      b.email.dispatchEvent(new Event('input', { bubbles: true }));
      clearHint(b);
      b.email.focus();
    });
    b.hint.hidden = false;
  }

  function clearHint(b) {
    b.hint.hidden = true;
    b.hint.textContent = '';
  }

  function showStatus(b, msg, kind) {
    b.status.textContent = msg;
    b.status.className = 'groovi-bridge__status' + (kind ? ' groovi-bridge__status--' + kind : '');
    b.status.hidden = false;
  }

  function setBusy(b, busy) {
    b.submit.disabled = busy;
    b.submit.textContent = busy ? COPY.sending : COPY.submit;
    b.root.classList.toggle('groovi-bridge--busy', busy);
  }

  // Hand off to the real Shopify form (pre-filled, kept in sync) and retire our
  // UI. No note of our own — the Shopify form shows its own error or success, so
  // the visitor sees exactly one, accurate message.
  function revealForm(b, formRoot) {
    setBusy(b, false);
    formRoot.classList.remove('groovi-shopify-form--offscreen');
    formRoot.classList.add('groovi-shopify-form--fallback');
    b.root.hidden = true;
  }

  function showSuccess(b) {
    setBusy(b, false);
    // The card's heading ("A primeira ___ do Brasil") + subheading are teaser
    // copy that reads oddly next to a confirmation, so hide them and let the
    // success message stand cleanly under the logo.
    var content = b.root.closest('.group-block-content');
    if (content) {
      var texts = content.querySelectorAll('.text-block');
      for (var i = 0; i < texts.length; i++) texts[i].style.display = 'none';
    }
    b.root.innerHTML = '<p class="groovi-bridge__success">' + COPY.success + '</p>';
  }

  // --- wiring ----------------------------------------------------------------

  function init() {
    var wrapper = document.querySelector(WRAPPER_SEL);
    if (!wrapper || wrapper.dataset.bridgeInit) return;
    var formRoot = wrapper.querySelector('[data-form-root]');
    if (!formRoot) return;
    wrapper.dataset.bridgeInit = '1';

    // Hide the real form (kept rendered so hCaptcha lives). Done in JS so that
    // if this script never runs, the native form stays visible (fallback a).
    formRoot.classList.add('groovi-shopify-form--offscreen');

    warmBundle();

    var b = buildForm();
    wrapper.appendChild(b.root);

    var last = { name: '', email: '', phone: '' };
    var typoDismissed = false;

    function mirror(key, input) {
      last[key] = input.value;
      var f = shadowFields(formRoot);
      if (f) setReal(f[key], input.value);
    }

    // Phone composition. +55 is only a DEFAULT for bare numbers (the Brazilian
    // majority); if the visitor types their own country code (leading "+"), we
    // pass it through untouched so other countries aren't blocked.
    function phoneRaw() { return (b.phone.value || '').trim(); }
    function phoneDigits() { return phoneRaw().replace(/\D/g, ''); }
    function phoneIsIntl() { return phoneRaw().charAt(0) === '+'; }
    function composedPhone() {
      var raw = phoneRaw();
      if (!raw) return '';
      if (phoneIsIntl()) return raw;            // explicit country code → as typed
      return phoneDigits() ? ('+55 ' + phoneDigits()) : '';
    }
    function phoneComplete() {
      var d = phoneDigits();
      if (phoneIsIntl()) return d.length >= 8;  // lenient for international
      return d.length === 10 || d.length === 11; // BR landline / mobile
    }

    // Ghost example shows only while the field is still just "+".
    var phoneWrap = b.phone.parentNode;
    function updateGhost() { phoneWrap.classList.toggle('is-ghost', b.phone.value === '+'); }
    updateGhost();

    b.name.addEventListener('input', function () { mirror('name', b.name); });
    b.phone.addEventListener('input', function () {
      updateGhost();
      last.phone = composedPhone();
      var f = shadowFields(formRoot);
      if (f) setReal(f.phone, last.phone);
    });
    b.email.addEventListener('input', function () {
      mirror('email', b.email);
      typoDismissed = false;
      clearHint(b);
    });
    b.email.addEventListener('blur', function () {
      var s = emailSuggestion(b.email.value);
      if (s) showHint(b, s);
    });

    // Flush any pre-mount typing once the real form appears.
    waitForMount(formRoot, function (f) {
      setReal(f.name, last.name);
      setReal(f.email, last.email);
      setReal(f.phone, last.phone);
    });

    b.root.addEventListener('submit', function (e) {
      e.preventDefault();
      if (b.submit.disabled) return;

      var name = b.name.value.trim();
      var email = b.email.value.trim();
      var phone = b.phone.value.trim();

      if (!name || !email || !phone) {
        showStatus(b, COPY.required, 'error');
        (!name ? b.name : !email ? b.email : b.phone).focus();
        return;
      }
      if (!syntaxOk(email)) {
        showStatus(b, COPY.emailInvalid, 'error');
        b.email.focus();
        return;
      }
      // Catch an incomplete number here so it fails gently in our form instead
      // of bouncing the visitor to the Shopify form.
      if (!phoneComplete()) {
        showStatus(b, COPY.phoneInvalid, 'error');
        b.phone.focus();
        return;
      }
      // Soft typo nudge: show once, let a second submit through.
      var s = emailSuggestion(email);
      if (s && !typoDismissed) {
        typoDismissed = true;
        showHint(b, s);
        b.email.focus();
        return;
      }

      showStatus(b, '', null);
      b.status.hidden = true;
      setBusy(b, true);

      waitForMount(formRoot, function (f) {
        setReal(f.name, name);
        setReal(f.email, email);
        setReal(f.phone, composedPhone());
        if (!f.submit || !f.form) { revealForm(b, formRoot); return; }
        observeOutcome(f,
          function () { showSuccess(b); },
          function () { revealForm(b, formRoot); }
        );
        try { f.submit.click(); } catch (err) { revealForm(b, formRoot); }
      }, function () {
        revealForm(b, formRoot);
      });
    });
  }

  // Runs at end of <body>, after the form wrapper is parsed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
